declare global {
  interface PromiseConstructor {
    withResolvers<T>(): {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: any) => void;
    };
  }
}

import './i18n';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { createPortal } from 'react-dom';
import '@/index.css';
import AppRouter from '@/routes/router.tsx';
import { store } from '@/app/store';
import { Provider } from 'react-redux';
import { type IconProps, ToastContainer } from 'react-toastify';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { patchPdfShadingFallback } from '@/lib/pdfShadingFallbackPatch';
import { captureSignupOrigin } from '@/lib/signupOrigin';

patchPdfShadingFallback();
captureSignupOrigin(window.location, document.referrer, new Date());

declare const __APP_VERSION__: string;

const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const applicationEnvironment = import.meta.env.MODE;
const isBugsnagEnabled = bugsnagApiKey !== undefined && bugsnagApiKey !== '';
const appVersion = import.meta.env.VITE_APP_VERSION ?? __APP_VERSION__;

Promise.withResolvers ??= function <T>() {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: any) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
};

if (isBugsnagEnabled) {
  Bugsnag.start({
    apiKey: bugsnagApiKey,
    appVersion,
    releaseStage: applicationEnvironment,
    enabledReleaseStages: ['production', 'staging'],
    plugins: [new BugsnagPluginReact()],
  });

  BugsnagPerformance.start({
    apiKey: bugsnagApiKey,
    attributeStringValueLimit: 4096,
    networkRequestCallback: (networkRequest) => {
      if (
        networkRequest != null &&
        (networkRequest?.url?.includes('.m3u8') || networkRequest?.url?.includes('.ts'))
      ) {
        return null;
      }
      return networkRequest;
    },
  });
}

const resolveErrorBoundary = () => {
  if (isBugsnagEnabled) {
    const reactPlugin = Bugsnag.getPlugin('react');
    if (reactPlugin) {
      return reactPlugin.createErrorBoundary(React);
    }
  }
  return ({ children }: { children: React.ReactNode }) => <>{children}</>;
};

const BugsnagErrorBoundary = resolveErrorBoundary();
const rootContainerElement = document.getElementById('root');

if (rootContainerElement === null) {
  throw new Error('Critical Error: Root container element not found.');
}

const applicationRoot = ReactDOM.createRoot(rootContainerElement, {
  onUncaughtError: (unhandledError: unknown, errorInformation: { componentStack?: string }) => {
    if (isBugsnagEnabled) {
      Bugsnag.notify(unhandledError as any, (event) => {
        event.addMetadata('react', {
          componentStack: errorInformation.componentStack,
        });
      });
    }
  },
  onCaughtError: (handledError: unknown, errorInformation: { componentStack?: string }) => {
    if (isBugsnagEnabled) {
      Bugsnag.notify(handledError as any, (event) => {
        event.severity = 'warning';
        event.addMetadata('react', {
          componentStack: errorInformation.componentStack,
        });
      });
    }
  },
});
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  console.log('Nouvelle version détectée, rechargement...');
  window.location.reload();
});
applicationRoot.render(
  <React.StrictMode>
    <Provider store={store}>
      <BugsnagErrorBoundary>
        <Suspense fallback={null}>
          <AppRouter />
          {createPortal(
            <ToastContainer
              position="top-center"
              hideProgressBar
              autoClose={3000}
              style={{ zIndex: 2147483647, pointerEvents: 'auto' }}
              toastStyle={{
                maxWidth: '450px',
                width: '100%',
                zIndex: 2147483647,
                background: '#3D3E4D',
                color: '#fff',
                borderRadius: '14px',
                padding: '16px 24px 16px 16px',
                justifyContent: 'flex-start',
              }}
              closeButton={({ closeToast }) => (
                <button
                  type="button"
                  className="Toastify__close-button"
                  onClick={closeToast}
                  aria-label="close"
                >
                  <X className="text-white" size={24} />
                </button>
              )}
              icon={(iconProperties: IconProps) => {
                if (iconProperties.type === 'success') {
                  return <CheckCircle2 className="size-6 text-success" />;
                }
                if (iconProperties.type === 'error') {
                  return <TriangleAlert className="size-6 text-error" />;
                }
                return undefined;
              }}
            />,
            document.body,
          )}
        </Suspense>
      </BugsnagErrorBoundary>
    </Provider>
  </React.StrictMode>,
);
